/**
 * ActivityFeedItemView Component
 *
 * Pure presentation component for displaying activity feed items.
 * Receives all processed data as props from ActivityFeedItemContainer.
 * @remarks
 * This is the View component in the Container/View pattern.
 * It contains no logic, hooks, or data transformation.
 * All interaction logic is handled by ActivityFeedItemContainer.
 * @example
 * ```tsx
 * <ActivityFeedItemView
 *   activityItem={item}
 *   bodyContent="Player added"
 *   activityIcon={PlusIcon}
 *   parsedParts={[{ text: "John", bold: true }]}
 *   isSmallScreen={false}
 *   onPress={handlePress}
 *   isDark={true}
 * />
 * ```
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { ActivityFeedFragment } from "@/generated/graphql";
import { formatTimeRelative } from "@/utils/formatDateBasedOnDays";

/**
 * Legacy activity type definition
 * @deprecated Use ActivityFeedFragment from GraphQL types
 */
export type TActivity = {
  id: string;
  code: string;
  title: string;
  subtitle: string;
  createdAt: string;
  isRead: boolean;
  isSeen: boolean;
  deeplink?: string;
};

/**
 * Styled text part with bold flag
 */
type StyledText = {
  text: string;
  bold: boolean;
};

/**
 * Returns a subtle background tint class for unread notification items.
 * The SE-5303 codemod turned the light/dark rgba pair (alpha 0.03 / 0.04) into
 * opacity steps that are not on the closed opacity scale, so the tint emitted
 * no CSS at all. Both alphas snap to the same on-scale step (0.05), and the
 * `status-info` token is already theme-aware, so the light/dark fork collapses.
 * @param isRead - Whether the notification has been read
 * @param _isDark - Unused; retained for the call-site signature
 * @returns NativeWind class string for unread tint, or empty string if read
 */
const getUnreadBgClass = (isRead: boolean, _isDark: boolean): string =>
  isRead ? "" : "bg-status-info/5";

/**
 * Returns the icon container background class.
 * Theme-aware via the surface-raised token (was an isDark hex ternary).
 * @param _isDark - Unused; retained for the call-site signature
 * @returns NativeWind class string
 */
const getIconBgClass = (_isDark: boolean): string => "bg-surface-raised";

/**
 * Returns the title text class for activity feed items.
 * Theme-aware via the content-primary token (was an isDark white/gray-900
 * ternary).
 * @param _isDark - Unused; retained for the call-site signature
 * @param isSmallScreen - Whether the screen is mobile sized
 */
const getActivityTitleClass = (
  _isDark: boolean,
  isSmallScreen: boolean
): string =>
  `flex-1 items-center text-title text-content-primary ${isSmallScreen ? "" : "opacity-90"}`;

/**
 * Props for ActivityFeedItemView
 */
interface Props {
  /** Activity item data from GraphQL */
  activityItem: ActivityFeedFragment;
  /** Processed body content text */
  bodyContent: string | null;
  /** Icon component to display for this activity type */
  activityIcon: React.ElementType;
  /** Theme-aware icon color class for this activity type */
  iconColor: string;
  /** Parsed text parts with bold styling information */
  parsedParts: StyledText[];
  /** Whether screen width is below mobile breakpoint */
  isSmallScreen: boolean;
  /** Callback when activity item is pressed */
  onPress: () => void;
  /** Whether dark theme is active */
  isDark: boolean;
}

/**
 * Pure presentation component for activity feed items
 * @param props - Component props
 * @param props.activityItem - Activity item data from GraphQL
 * @param props.isSmallScreen - Whether screen width is below mobile breakpoint
 * @param props.bodyContent - Processed body content text
 * @param props.activityIcon - Icon component to display for this activity type
 * @param props.iconColor - Theme-aware icon color class for this activity type
 * @param props.parsedParts - Parsed text parts with bold styling information
 * @param props.onPress - Callback when activity item is pressed
 * @param props.isDark - Whether dark theme is active
 */
const ActivityFeedItemView = ({
  activityItem,
  isSmallScreen,
  bodyContent,
  activityIcon,
  iconColor,
  parsedParts,
  onPress,
  isDark,
}: Props) => (
  <Pressable
    onPress={onPress}
    className="web:hover:bg-surface-inverse/5 active:opacity-80"
  >
    <Stack
      direction="horizontal"
      className={`relative flex-row items-center ${isSmallScreen ? "gap-3" : "gap-4 p-2"} rounded-md ${getUnreadBgClass(activityItem.isRead, isDark)}`}
    >
      <Box
        className={`items-center justify-center self-center rounded-full p-2 ${getIconBgClass(isDark)}`}
      >
        <Icon
          as={activityIcon}
          size={isSmallScreen ? "md" : "xl"}
          className={iconColor}
          // @ts-expect-error -- the library is mis-typed
          strokeWidth={2.5}
        />
      </Box>
      <Stack className="flex-1 justify-center">
        <Stack direction="horizontal" className="justify-between gap-2">
          <Text
            variant="unstyled"
            className={getActivityTitleClass(isDark, isSmallScreen)}
          >
            {parsedParts.map((part, index) => (
              <Text variant="unstyled" key={index}>
                {part.text}
              </Text>
            ))}
          </Text>

          {!activityItem.isRead && (
            <Box className="size-2 rounded-full bg-accent-primary translate-y-1" />
          )}
        </Stack>
        {isSmallScreen ? (
          <Stack className="gap-2">
            {Boolean(bodyContent) && (
              <Text
                variant="unstyled"
                className="text-title-sm text-content-secondary"
              >
                {bodyContent}
              </Text>
            )}

            <Text variant="caption" className="text-content-muted">
              {formatTimeRelative(activityItem.createdAt)?.toUpperCase()}
            </Text>
          </Stack>
        ) : (
          <Stack
            direction="horizontal"
            className="items-end justify-between gap-2"
          >
            <Text
              variant="unstyled"
              className="flex-1 text-title-sm text-content-secondary"
            >
              {bodyContent || ""}
            </Text>

            <Stack className="self-end">
              <Text variant="caption" className="text-content-muted">
                {formatTimeRelative(activityItem.createdAt)}
              </Text>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  </Pressable>
);

ActivityFeedItemView.displayName = "ActivityFeedItemView";

export default memo(ActivityFeedItemView);
